import { type FC } from "react";
import { Loader2Icon, CheckCircle2Icon, HelpCircleIcon } from "lucide-react";
import { Card } from "../ui/card";
import { cn } from "@/lib/utils";

// Custom tool component for similar_column_values tool
const SimilarColumnValuesToolCallComponent: FC<{
  toolName: string;
  argsText: string;
  result?: unknown;
  status?: { type: "running" | "complete" | "incomplete"; reason?: string };
  args?: any;
  toolCallId?: string;
  [key: string]: any; // Accept any additional props
}> = ({ argsText, result, status }) => {
  const isRunning = status?.type === "running" || (status === undefined && result === undefined);
  const isError = status?.type === "incomplete" && status?.reason === "error";
  // Tool is complete only when status is explicitly 'complete' from backend
  const isComplete = status?.type === "complete" && !isError;

  // Parse args to get the value the user asked about
  let userValue: string = "";
  let hasValidArgs = false;
  try {
    // Try parsing as JSON first
    const parsedArgs = typeof argsText === 'string' ? JSON.parse(argsText) : argsText;
    userValue = parsedArgs?.value || "";
    // Check if we have a valid value (not empty string)
    hasValidArgs = !!userValue;
  } catch (e) {
    // If parsing fails, try to extract from argsText string using regex
    if (typeof argsText === 'string') {
      const valueMatch = argsText.match(/"value"\s*:\s*"([^"]+)"/);
      if (valueMatch) {
        userValue = valueMatch[1];
        hasValidArgs = !!userValue;
      } else {
        // Last resort: try to find any quoted value
        const quotedMatch = argsText.match(/"([^"]+)"/);
        if (quotedMatch) {
          userValue = quotedMatch[1];
          hasValidArgs = !!userValue;
        }
      }
    }
  }

  // Parse result to get the found value
  let foundValue: string = "";
  if (result !== undefined && isComplete) {
    try {
      const parsed = typeof result === 'string' ? JSON.parse(result) : result;
      const similarValues = parsed?.similar_values || [];
      // Use the first similar value if available, otherwise use the original value
      foundValue = similarValues.length > 0 ? String(similarValues[0]) : userValue;
    } catch (e) {
      // If parsing fails, try to extract from result string
      if (typeof result === 'string') {
        const similarValuesMatch = result.match(/"similar_values"\s*:\s*\["([^"]+)"/);
        if (similarValuesMatch) {
          foundValue = similarValuesMatch[1];
        } else {
          foundValue = userValue;
        }
      } else {
        foundValue = userValue;
      }
    }
  }

  return (
    <Card>
      <div className={cn(
        "p-3",
        isRunning ? "bg-primary/10" : "bg-muted/50"
      )}>
        <div className="flex items-center gap-2">
          {isRunning ? (
            <div className="flex items-center gap-2">
              <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
              <p className="text-sm m-0 text-foreground">
                {hasValidArgs ? (
                  <>User asked about <strong>{userValue}</strong>. Checking the data for the value to use</>
                ) : (
                  <>Analyzing user's question...</>
                )}
              </p>
            </div>
          ) : isComplete ? (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <HelpCircleIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <p className="text-sm m-0 text-muted-foreground">
                  User asked about <strong className="text-foreground">{userValue || "..."}</strong>. Checking the data for the value to use
                </p>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2Icon className="h-4 w-4 text-success flex-shrink-0" />
                <p className="text-sm m-0 text-foreground">
                  Found <strong>{foundValue || userValue || "..."}</strong>. Using that in my analysis.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
              <p className="text-sm m-0 text-foreground">
                {hasValidArgs ? (
                  <>User asked about <strong>{userValue}</strong>. Checking the data for the value to use</>
                ) : (
                  <>Analyzing user's question...</>
                )}
              </p>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

// Type assertion to match assistant-ui's expected type
export const SimilarColumnValuesToolCall = SimilarColumnValuesToolCallComponent as React.ComponentType<any>;

